"use client"

import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { cn } from "@/lib/utils"
import { saveTokens, normalizeUserFromV3, getAllAccounts, switchAccount, removeAccount, type User, type StoredAccount } from "@/lib/auth"
import { useUser } from "@/components/user-context"
import { login, getUser, trackAnalyticsEvent, ApiError, type LoginRequest, type LoginResponse } from "@/lib/api-v3"
import { debugLog, debugWarn } from "@/lib/debug"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { InteractiveGradientButton } from "@/components/interactive-gradient-button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { IconCheck, IconChevronDown, IconLogout, IconUserPlus } from "@tabler/icons-react"
import { toast } from "sonner"
import { LogoutConfirmDialog } from "@/components/logout-confirm-dialog"

interface LoginFormProps extends React.ComponentProps<"form"> {
  isDialog?: boolean
  onSuccess?: (user: User, tokens: LoginResponse) => void
}

export function LoginForm({ className, isDialog = false, onSuccess, ...props }: LoginFormProps) {
  const [loginValue, setLoginValue] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{login?: string; password?: string; general?: string}>({})

  // Состояния для выбора аккаунта (при 401 или мультиаккаунте)
  const [accounts, setAccounts] = useState<StoredAccount[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [isNewAccount, setIsNewAccount] = useState(true)
  const [isCheckingToken, setIsCheckingToken] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [logoutTarget, setLogoutTarget] = useState<{ id: string; name: string; login: string } | null>(null)

  const router = useRouter()
  const { updateUser } = useUser()

  useEffect(() => {
    // В диалоге добавления нового аккаунта — всегда пустая форма
    if (isDialog) return

    const allAccounts = getAllAccounts()
    setAccounts(allAccounts)

    if (allAccounts.length > 0) {
      const activeId = localStorage.getItem("activeAccountId") || allAccounts[0].id
      const activeAccount = allAccounts.find(a => a.id === activeId) || allAccounts[0]

      setSelectedAccountId(activeAccount.id)
      setLoginValue(activeAccount.user.login)
      setIsNewAccount(false)
    }
  }, [isDialog])

  const selectedAccount = accounts.find(a => a.id === selectedAccountId) ?? null

  const handleAccountSelect = async (accountId: string) => {
    setIsDropdownOpen(false)

    if (accountId === 'new') {
      setIsNewAccount(true)
      setSelectedAccountId(null)
      setLoginValue("")
      setPassword("")
      setErrors({})
      return
    }

    const account = accounts.find(a => a.id === accountId)
    if (!account) return

    setIsNewAccount(false)
    setSelectedAccountId(account.id)
    setLoginValue(account.user.login)
    setPassword("")
    setErrors({})

    // Проверяем токен выбранного аккаунта
    setIsCheckingToken(true)
    try {
      debugLog('auth', `🔄 Проверка токена для ${account.user.login}...`)
      await getUser(account.tokens.accessToken)

      // Токен валиден! Переключаемся и заходим
      debugLog('auth', `✅ Токен валиден, входим как ${account.user.login}`)
      switchAccount(account.id)
      updateUser(account.user)

      // 📊 Аналитика: авторизация завершена (переключение аккаунта)
      await trackAnalyticsEvent(account.tokens.accessToken, {
        name: 'auth_completed',
        type: 0,
        value: null,
        timeZone: Math.round(-new Date().getTimezoneOffset() / 60),
        workspaceId: null,
      })

      if (isDialog && onSuccess) {
        onSuccess(account.user, account.tokens as any)
      } else {
        toast.success("Вход выполнен успешно!")
        setTimeout(() => {
          router.push("/analytics")
        }, 500)
      }
    } catch (error) {
      // Токен невалиден (например, 401) - просто ждем ввода пароля
      debugLog('auth', `❌ Токен невалиден, требуется пароль`)
      toast("Сессия истекла", {
        description: "Пожалуйста, введите пароль",
        duration: 3000,
      })
    } finally {
      setIsCheckingToken(false)
    }
  }

  const handleLogoutClick = (account: StoredAccount, e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDropdownOpen(false)
    setLogoutTarget({ id: account.id, name: account.user.name, login: account.user.login })
  }

  const handleLogoutConfirm = () => {
    if (!logoutTarget) return
    try {
      removeAccount(logoutTarget.id)
      const remaining = getAllAccounts()
      setAccounts(remaining)
      if (selectedAccountId === logoutTarget.id) {
        if (remaining.length > 0) {
          setSelectedAccountId(remaining[0].id)
          setLoginValue(remaining[0].user.login)
          setIsNewAccount(false)
        } else {
          setSelectedAccountId(null)
          setLoginValue("")
          setIsNewAccount(true)
        }
      }
    } catch (error) {
      toast.error("Ошибка при выходе из аккаунта")
    } finally {
      setLogoutTarget(null)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: {login?: string; password?: string} = {}

    if (!loginValue.trim()) {
      newErrors.login = "Введите логин"
    }

    if (!password.trim()) {
      newErrors.password = "Введите пароль"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleLogin = async (loginData: LoginRequest): Promise<void> => {
    try {
      debugLog('auth', "🚀 Авторизация...")

      const tokenData = await login(loginData)
      debugLog('auth', "✅ Токены получены")

      debugLog('user', "👤 Получение пользователя...")
      const userData = normalizeUserFromV3(await getUser(tokenData.accessToken))
      debugLog('user', "✅ Пользователь получен:", userData.name)

      // 📊 Аналитика: авторизация завершена
      await trackAnalyticsEvent(tokenData.accessToken, {
        name: 'auth_completed',
        type: 0,
        value: null,
        timeZone: Math.round(-new Date().getTimezoneOffset() / 60),
        workspaceId: null,
      })

      if (isDialog) {
        if (onSuccess) {
          onSuccess(userData, tokenData)
        }
      } else {
        saveTokens(tokenData, userData)
        updateUser(userData)

        toast.success("Вход выполнен успешно!", {
          description: "Добро пожаловать в систему",
          duration: 2000,
        })

        setTimeout(() => {
          router.push("/analytics")
        }, 1000)
      }

    } catch (error) {
      debugLog('auth', "❌ Ошибка авторизации:", error)
      handleAuthError(error)
    }
  }

  const handleAuthError = (error: unknown): void => {
    if (error instanceof ApiError) {
      handleApiError(error)
    } else {
      handleNetworkError(error)
    }
  }

  const handleApiError = (error: ApiError): void => {
    let showToast = true
    let errorMessage = "Произошла ошибка"

    switch (error.status) {
      case 400:
        errorMessage = "Неверный формат данных"
        setErrors({ general: "Проверьте введенные данные" })
        break

      case 401:
        setErrors({
          password: "Неверный логин или пароль"
        })
        showToast = false
        break

      case 403:
        errorMessage = "Доступ запрещен"
        setErrors({ general: "Нет прав для входа" })
        break

      case 404:
        errorMessage = "Сервис недоступен"
        setErrors({ general: "Сервер недоступен" })
        break

      case 429:
        errorMessage = "Слишком много попыток"
        setErrors({ general: "Попробуйте позже" })
        break

      case 500:
        errorMessage = "Ошибка сервера"
        setErrors({ general: "Внутренняя ошибка сервера" })
        break

      case 502:
      case 503:
      case 504:
        errorMessage = "Сервер недоступен"
        setErrors({ general: "Сервер временно недоступен" })
        break

      default:
        errorMessage = `Ошибка ${error.status}`
        setErrors({ general: `Ошибка ${error.status}` })
    }

    if (showToast) {
      toast.error(errorMessage, { duration: 5000 })
    }
  }

  const handleNetworkError = (error: unknown): void => {
    setErrors({ general: "Не удалось авторизоавться. Проверте подключение к интернету" })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    if (!navigator.onLine) {
      setErrors({ general: "Нет интернета" })
      toast.error("Проверьте подключение к интернету")
      return
    }

    if (!validateForm()) {
      toast.error("Исправьте ошибки в форме")
      return
    }

    setIsLoading(true)

    try {
      await handleLogin({ login: loginValue.trim(), password })
    } finally {
      setIsLoading(false)
    }
  }

  const showSupportToast = () => {
    toast("Обратитесь в техподдержку", {
      description: "Для восстановления пароля или регистрации",
      duration: 6000,
    })
  }

  const showAccountSelector = !isDialog && accounts.length > 0 && !isNewAccount

  return (
    <>
      <form onSubmit={handleSubmit} className={cn("flex flex-col gap-6", className)} {...props}>
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Войти в аккаунт</h1>
          <p className="text-muted-foreground text-sm">
            Введите ваш логин и пароль для входа
          </p>
        </div>

        {errors.general && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3">
            <p className="text-red-600 dark:text-red-400 text-sm">{errors.general}</p>
          </div>
        )}

        <div className="grid gap-6">
          <div className="grid gap-3">
            <Label htmlFor="login" className={errors.login ? "text-red-600 dark:text-red-400" : ""}>
              Логин
            </Label>

            {showAccountSelector ? (
              <>
                {/* Кастомный дропдаун с аватарами и кнопкой выхода */}
                <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={isLoading || isCheckingToken}
                      className={cn(
                        "flex items-center gap-2 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background",
                        "hover:bg-accent/50 transition-colors text-left",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        errors.login ? "border-red-500 focus-visible:ring-red-500" : "border-input",
                      )}
                    >
                      {selectedAccount ? (
                        <>
                          <Avatar className="h-6 w-6 rounded-md flex-shrink-0">
                            <AvatarImage src="/avatars/shadcn.jpg" alt={selectedAccount.user.name} />
                            <AvatarFallback className="rounded-md text-xs">
                              {selectedAccount.user.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="flex-1 truncate font-medium">{selectedAccount.user.name}</span>
                        </>
                      ) : (
                        <span className="flex-1 text-muted-foreground">Выберите аккаунт</span>
                      )}
                      <IconChevronDown className={cn("size-4 text-muted-foreground flex-shrink-0 transition-transform", isDropdownOpen && "rotate-180")} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={4}
                    className="p-1 w-[var(--radix-popover-trigger-width)]"
                  >
                    <div className="max-h-48 overflow-y-auto">
                    {accounts.map((acc) => {
                      const isSelected = acc.id === selectedAccountId
                      return (
                        <button
                          key={acc.id}
                          type="button"
                          onClick={() => handleAccountSelect(acc.id)}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                            "hover:bg-accent hover:text-accent-foreground transition-colors",
                            "focus-visible:outline-none focus-visible:bg-accent",
                          )}
                        >
                          <Avatar className="h-7 w-7 rounded-md flex-shrink-0">
                            <AvatarImage src="/avatars/shadcn.jpg" alt={acc.user.name} />
                            <AvatarFallback className="rounded-md text-xs">
                              {acc.user.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-medium truncate">{acc.user.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{acc.user.login}</div>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {isSelected && <IconCheck className="size-4" />}
                            <button
                              type="button"
                              onClick={(e) => handleLogoutClick(acc, e)}
                              className="p-1 rounded-sm text-muted-foreground hover:text-red-500 transition-colors"
                              title="Выйти из аккаунта"
                            >
                              <IconLogout className="size-3.5" />
                            </button>
                          </div>
                        </button>
                      )
                    })}
                    </div>

                    {/* Разделитель и "Войти ещё в аккаунт" */}
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      onClick={() => handleAccountSelect('new')}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                        "hover:bg-accent hover:text-accent-foreground transition-colors",
                        "focus-visible:outline-none focus-visible:bg-accent",
                      )}
                    >
                      <IconUserPlus className="size-4 flex-shrink-0" />
                      <span>Войти ещё в аккаунт</span>
                    </button>
                  </PopoverContent>
                </Popover>
              </>
            ) : (
              <>
                <Input
                  id="login"
                  type="text"
                  placeholder="Введите логин"
                  value={loginValue}
                  onChange={(e) => setLoginValue(e.target.value)}
                  disabled={isLoading || isCheckingToken}
                  className={errors.login ? "border-red-500 focus-visible:ring-red-500" : ""}
                  required
                />
              </>
            )}

            {errors.login && (
              <p className="text-red-600 dark:text-red-400 text-sm">{errors.login}</p>
            )}
          </div>

          <div className="grid gap-3">
            <div className="flex items-center">
              <Label htmlFor="password" className={errors.password ? "text-red-600 dark:text-red-400" : ""}>
                Пароль
              </Label>
              <button
                type="button"
                onClick={showSupportToast}
                disabled={isLoading}
                className="ml-auto text-sm underline-offset-4 hover:underline cursor-pointer disabled:opacity-50"
              >
                Забыли пароль?
              </button>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isCheckingToken}
              className={errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}
              required
            />
            {errors.password && (
              <p className="text-red-600 dark:text-red-400 text-sm">{errors.password}</p>
            )}
          </div>

          <InteractiveGradientButton
            type="submit"
            className="w-full"
            disabled={isLoading || isCheckingToken}
          >
            {isLoading || isCheckingToken ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isCheckingToken ? "Проверка..." : "Входим..."}
              </div>
            ) : (
              "Войти"
            )}
          </InteractiveGradientButton>
        </div>

        {!isDialog && (
          <div className="text-center text-sm">
            Нет аккаунта?{" "}
            <button
              type="button"
              onClick={showSupportToast}
              disabled={isLoading}
              className="underline underline-offset-4 cursor-pointer hover:text-primary disabled:opacity-50"
            >
              Зарегистрироваться
            </button>
          </div>
        )}
      </form>

      <LogoutConfirmDialog
        open={logoutTarget !== null}
        onOpenChange={(open) => { if (!open) setLogoutTarget(null) }}
        accountName={logoutTarget?.name ?? ""}
        accountLogin={logoutTarget?.login ?? ""}
        onConfirm={handleLogoutConfirm}
      />
    </>
  )
}
